// A deep link has to survive the trip through the login page. The dashboard
// pack records where the user was headed before bouncing them to /app/login,
// and the auth pack reads it back after sign-in. They are two separate page
// loads (see DashboardController#set_application_pack), so a query param is the
// only channel available — nothing in memory outlives the navigation.
export const RETURN_TO_PARAM = 'returnTo';

const ABSOLUTE_URL = /^[a-z][a-z0-9+.-]*:/i;
const ACCOUNT_SCOPED_PATH = /^\/app\/accounts\/(\d+)(?:[/?#]|$)/;
const BACKSLASH = String.fromCodePoint(92);
// Any absolute origin serves as the base for resolving a relative reference.
// It never reaches the browser and is never fetched; it only lets the url parser
// normalise for us, and only its equality with resolved.origin is ever read.
// https, not http, purely to satisfy the scheme rule on a value that is inert.
const RESOLUTION_BASE = 'https://cloudchat.invalid';

// Control characters (a smuggled newline can split a header) and backslashes
// (browsers normalise them into slashes, so a leading "/\" leaves the origin).
// Scanned by code point rather than matched by regex to keep literal control
// characters out of the source.
const hasUnsafeCharacters = value => {
  if (value.includes(BACKSLASH)) return true;

  for (let index = 0; index < value.length; index += 1) {
    const code = value.codePointAt(index);
    if (code < 0x20 || code === 0x7f) return true;
  }

  return false;
};

const isAuthPath = pathname =>
  pathname === '/app/login' || pathname.startsWith('/app/auth/');

/**
 * Narrows an arbitrary string down to a path we are willing to send the browser
 * to after login, or null when it is not one.
 *
 * The value reaches us from a query param, so it is attacker-controlled: the
 * checks below exist to keep the login page from becoming an open redirect.
 */
export const sanitizeReturnTo = raw => {
  if (typeof raw !== 'string') return null;

  const candidate = raw.trim();
  if (!candidate) return null;

  if (hasUnsafeCharacters(candidate)) return null;

  // An absolute url of any scheme, "javascript:" and "data:" included.
  if (ABSOLUTE_URL.test(candidate)) return null;

  if (!candidate.startsWith('/')) return null;

  // Judge the path the browser will actually visit, not the string we were
  // handed. "/app/x/%2e%2e/accounts/9/y" carries no literal ".." yet the url
  // parser collapses it to "/app/accounts/9/y": checking the raw string would
  // let it slip past both the prefix test below and the account gate in
  // getLoginRedirectURL, which reads the account id straight out of this path.
  let resolved;
  try {
    resolved = new URL(candidate, RESOLUTION_BASE);
  } catch {
    return null;
  }

  // A leading "//" is parsed as an authority, so the origin can still move even
  // though the input looked like nothing but a path.
  if (resolved.origin !== RESOLUTION_BASE) return null;

  // Everything we serve lives under /app/.
  if (!resolved.pathname.startsWith('/app/')) return null;

  // The auth pack itself is not a destination: honouring it would bounce the
  // user straight back to the page they just finished with.
  if (isAuthPath(resolved.pathname)) return null;

  // Hand back the normalised form, so every caller downstream reads the same
  // path the browser will resolve.
  return `${resolved.pathname}${resolved.search}${resolved.hash}`;
};

/**
 * The numeric account id a dashboard path is scoped to, or null for paths that
 * are not account-scoped (/app/profile/settings and friends).
 *
 * Only meaningful for a path that came out of sanitizeReturnTo: a raw,
 * un-normalised path can hide its real account behind a dot segment.
 */
export const accountIdFromPath = path => {
  const match = ACCOUNT_SCOPED_PATH.exec(path || '');
  return match ? Number(match[1]) : null;
};
